import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, GraduationCap, CalendarDays, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Schedule() {
  const [exams, setExams] = useState([]);
  const [events, setEvents] = useState([]);
  const [ex, setEx] = useState({ subject: "", date: "", time: "", topic: "" });
  const [ev, setEv] = useState({ title: "", date: "", type: "event", description: "" });

  const load = () => {
    api.get("/exams").then((r) => setExams(r.data));
    api.get("/events").then((r) => setEvents(r.data));
  };
  useEffect(() => { load(); }, []);

  const addExam = async (e) => {
    e.preventDefault();
    if (!ex.subject.trim() || !ex.date) return;
    await api.post("/exams", ex);
    setEx({ subject: "", date: "", time: "", topic: "" });
    toast.success("Exam added");
    load();
  };
  const addEvent = async (e) => {
    e.preventDefault();
    if (!ev.title.trim() || !ev.date) return;
    await api.post("/events", ev);
    setEv({ title: "", date: "", type: "event", description: "" });
    toast.success("Event added");
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Exams */}
      <div className="space-y-4">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><GraduationCap className="w-5 h-5 text-indigo-500" /> Exam Schedule</h3>
        <form onSubmit={addExam} className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="exam-form">
          <Input data-testid="exam-subject-input" placeholder="Subject" value={ex.subject} onChange={(e) => setEx({ ...ex, subject: e.target.value })} className="rounded-xl" />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="exam-date-input" type="date" value={ex.date} onChange={(e) => setEx({ ...ex, date: e.target.value })} className="rounded-xl" />
            <Input data-testid="exam-time-input" type="time" value={ex.time} onChange={(e) => setEx({ ...ex, time: e.target.value })} className="rounded-xl" />
          </div>
          <Input data-testid="exam-topic-input" placeholder="Topic / syllabus (optional)" value={ex.topic} onChange={(e) => setEx({ ...ex, topic: e.target.value })} className="rounded-xl" />
          <Button data-testid="exam-add-btn" type="submit" className="rounded-xl bg-indigo-600 hover:bg-indigo-700 w-full"><Plus className="w-4 h-4 mr-1" /> Add exam</Button>
        </form>
        <div className="space-y-2" data-testid="exam-list">
          {exams.length === 0 && <p className="text-sm text-slate-400">No exams scheduled.</p>}
          {exams.map((e, i) => (
            <motion.div key={e.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 flex flex-col items-center justify-center shrink-0">
                <span className="text-[10px] font-bold text-indigo-500 uppercase">{new Date(e.date).toLocaleDateString("en-US", { month: "short" })}</span>
                <span className="text-lg font-bold text-indigo-700 leading-none">{new Date(e.date).getDate()}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900 text-sm flex items-center gap-2">{e.subject}
                  {e.source === "official" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-600">SCHOOL</span>}
                </p>
                <p className="text-xs text-slate-500">{e.topic || "General"} {e.time && `· ${e.time}`}</p>
              </div>
              {e.source !== "official" && (
                <button data-testid={`exam-delete-${e.id}`} onClick={async () => { await api.delete(`/exams/${e.id}`); load(); }} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Events */}
      <div className="space-y-4">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-amber-500" /> School Events & Holidays</h3>
        <form onSubmit={addEvent} className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="event-form">
          <Input data-testid="event-title-input" placeholder="Title (e.g. Diwali Break)" value={ev.title} onChange={(e) => setEv({ ...ev, title: e.target.value })} className="rounded-xl" />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="event-date-input" type="date" value={ev.date} onChange={(e) => setEv({ ...ev, date: e.target.value })} className="rounded-xl" />
            <Select value={ev.type} onValueChange={(v) => setEv({ ...ev, type: v })}>
              <SelectTrigger data-testid="event-type-select" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="event">Event</SelectItem>
                <SelectItem value="holiday">Holiday</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input data-testid="event-desc-input" placeholder="Description (optional)" value={ev.description} onChange={(e) => setEv({ ...ev, description: e.target.value })} className="rounded-xl" />
          <Button data-testid="event-add-btn" type="submit" className="rounded-xl bg-amber-500 hover:bg-amber-600 w-full"><Plus className="w-4 h-4 mr-1" /> Add event</Button>
        </form>
        <div className="space-y-2" data-testid="event-list">
          {events.length === 0 && <p className="text-sm text-slate-400">No events added.</p>}
          {events.map((e, i) => (
            <motion.div key={e.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${e.type === "holiday" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                {e.type === "holiday" ? <PartyPopper className="w-5 h-5" /> : <CalendarDays className="w-5 h-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900 text-sm flex items-center gap-2">{e.title}
                  {e.source === "official" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-600">SCHOOL</span>}
                </p>
                <p className="text-xs text-slate-500">{new Date(e.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}{e.description && ` · ${e.description}`}</p>
              </div>
              {e.source !== "official" && (
                <button data-testid={`event-delete-${e.id}`} onClick={async () => { await api.delete(`/events/${e.id}`); load(); }} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
